'use client';

import { ElementProps } from '@/components/sections/builder/types';
import CardProductBuilder from '@/components/sections/builder/elements/body/card-product';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { cn, hexToRgb } from '@/lib/utils';
import { useParams } from 'next/navigation';
import Cookies from 'js-cookie';
import SkeletonProductList from '@/components/sections/builder/elements/body/product-grid/skeleton';
import { useGetRelatedProduct } from '@/hooks/api/storefront/product';
import { RelatedProductElementDataType } from '@/components/sections/builder/elements/body/related-product/type';

export default function ListProductRelated({ element, preview, theme, host }: ElementProps) {
  const params = useParams();
  const customerToken = Cookies.get('accessToken');
  const locationId = Cookies.get('_location_id');
  const slug = params.slug as string;

  const data = element.data as RelatedProductElementDataType;

  const queryParams = {
    slug,
    hostname: host as string,
    page: 1,
    pageSize: data.limit || 4,
    limit: data.limit || 4,
  };

  const { data: productData, isLoading } = useGetRelatedProduct(
    queryParams,
    customerToken as string,
    locationId as string
  );

  const products = productData?.data || [];

  return (
    <>
      <section
        className={cn(
          'background-color-default',
          data.background?.type === 'color' && 'background-color',
          data.background?.type === 'image' && 'background-image'
        )}
      >
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div className='product-list-padding relative'>
            {data.overlay && data.background?.type !== 'image' && (
              <div
                className={cn(
                  'overlay absolute inset-0',
                  (data.fullWidth || data.background?.backgroundLayout === 'full_width') &&
                    'full_width_product_list'
                )}
              />
            )}
            <div className='relative p-3'>
              <div
                className={cn(
                  'flex w-full flex-col',
                  data.contentAlign === 'left' && '[&>*]:text-left',
                  data.contentAlign === 'center' && '[&>*]:text-center',
                  data.contentAlign === 'right' && '[&>*]:text-right'
                )}
              >
                <div className={cn('mb-4', data.positionViewAll === 'top' && 'w-full')}>
                  <div
                    className={cn(
                      'flex items-center gap-2 md:gap-3 xl:gap-4',
                      data.contentAlign === 'left' && 'justify-between',
                      data.contentAlign === 'center' && 'justify-center',
                      data.contentAlign === 'right' && 'flex-row-reverse justify-between'
                    )}
                  >
                    <h2
                      className={cn(
                        'theme_font_heading text-base font-bold tracking-tight',
                        data.headingSize === 'large' &&
                          'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
                        data.headingSize === 'medium' &&
                          'text-lg sm:text-xl md:text-2xl lg:text-3xl',
                        data.headingSize === 'small' &&
                          'text-base sm:text-lg md:text-xl lg:text-2xl'
                      )}
                      data-animation='true'
                      dangerouslySetInnerHTML={{ __html: data.headingLabel as string }}
                    />
                  </div>

                  {data.textLabel && (
                    <div
                      className={cn(
                        'minimal-tiptap-editor tiptap ProseMirror',
                        data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                        data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
                        data.textStyle === 'uppercase' &&
                          'text-xs uppercase md:text-sm lg:text-base'
                      )}
                      data-animation='true'
                      dangerouslySetInnerHTML={{
                        __html: data.textLabel as string,
                      }}
                    />
                  )}
                </div>

                <div
                  className={cn(
                    `product-list-padding grid-cols-${data.totalColMobile} sm:grid-cols-${data.totalCol} md:grid-cols-${data.totalCol} lg:grid-cols-${data.totalCol} theme_layout_space_grid_horizontal theme_layout_space_grid_vertical grid h-full w-full place-content-center items-stretch`,
                    (data.fullWidth || data.background?.backgroundLayout === 'full_width') &&
                      'full_width_product_list',
                    data.enableCarousel && 'grid md:hidden',
                    data.size === 'small' &&
                      '[&>article>div>div>div]:text-xs [&>article>div>div>h2]:text-base [&>article>div>div>p]:text-xs [&>article>div>div>span]:text-xs',
                    data.size === 'medium' &&
                      '[&>article>div>div>div]:text-sm [&>article>div>div>h2]:text-lg [&>article>div>div>p]:text-sm [&>article>div>div>span]:text-sm',
                    data.size === 'large' &&
                      '[&>article>div>div>div]:text-sm [&>article>div>div>h2]:text-lg [&>article>div>div>p]:text-sm [&>article>div>div>span]:text-sm'
                  )}
                >
                  {isLoading
                    ? Array.from({ length: data.limit as number }).map((_, index) => (
                        <SkeletonProductList key={index} />
                      ))
                    : products.map((product) => (
                        <CardProductBuilder
                          data={data}
                          product={product}
                          key={product.id}
                          element={element}
                          preview={preview}
                          theme={theme}
                        />
                      ))}
                </div>
                <div
                  className={cn(
                    `product-list-padding hidden place-content-center grid-cols-${data.totalColMobile} sm:grid-cols-${data.totalCol} md:grid-cols-${data.totalCol} lg:grid-cols-${data.totalCol}`,
                    (data.fullWidth || data.background?.backgroundLayout === 'full_width') &&
                      'full_width_product_list',
                    data.enableCarousel && 'md:block'
                  )}
                >
                  <Carousel className='max-w-full' opts={{ loop: true }}>
                    <CarouselContent>
                      {isLoading
                        ? Array.from({ length: 6 }).map((_, index) => (
                            <SkeletonProductList key={index} />
                          ))
                        : products.map((product) => (
                            <CarouselItem className={`flex_basis_${element.id}`} key={product.id}>
                              <CardProductBuilder
                                data={data}
                                product={product}
                                element={element}
                                preview={preview}
                              />
                            </CarouselItem>
                          ))}
                    </CarouselContent>
                    <CarouselPrevious className='md:left-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
                    <CarouselNext className='md:right-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
                  </Carousel>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .product-list-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
            .full_width_product_list {
              width: calc(100vw - 20px);
              margin-left: calc(-50vw + 50% + 10px);
            }
          }
          .card-rounded {
            border-radius: ${data.rounded || 0}px;
          }
          .overlay {
            background-color: ${data.overlay
              ? hexToRgb(data.overlay, Number(data.overlayPercentage || 100))
              : 'transparent'};
          }
          .background-color-default {
            background-color: ${data.colorScheme?.background || 'white'};
            color: ${data.colorScheme?.text};
          }
          .background-color {
            background-color: ${data.background?.backgroundColor};
            --tw-bg-opacity: ${data.background?.backgroundOpacity};
          }
          .background-image {
            background-image: url(${data.background?.backgroundImage});
            background-repeat: no-repeat;
            background-size: ${data.background?.backgroundSize};
            background-position: ${data.background?.backgroundPosition};
            background-attachment: ${data.background?.backgroundAttachment === 'fixed'
              ? 'unset'
              : 'fixed'};
          }
        `}
      </style>
      <style jsx global>
        {`
          .flex_basis_${element.id} {
            flex-basis: ${100 / data.totalCol || 1}% !important;
          }
        `}
      </style>
    </>
  );
}
